
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiPrinter, FiEdit, FiTrash2, FiFileText, FiRefreshCw, FiX } from 'react-icons/fi';
import { database } from '../../Firebase';
import { ref, push, onValue, update } from 'firebase/database';
import Namuna7Receipt from './Namuna7Receipt';
import Namuna17Report from './Namuna17Report';

const LoanManagement = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { grampanchayatName, arthikVarshLabel } = location.state || {}; // Assuming current Financial Year context

    const [printMode, setPrintMode] = useState(null); // 'namuna12', 'namuna17', 'namuna7'

    const [view, setView] = useState('list'); // 'list', 'repayment_history', 'repayment_form', 'new'
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [selectedRepayment, setSelectedRepayment] = useState(null); // For printing receipt
    const [searchTerm, setSearchTerm] = useState('');
    
    // Data States
    const [loans, setLoans] = useState([]);
    const [persons, setPersons] = useState([]);
    const [allRepayments, setAllRepayments] = useState([]); // Store all repayments
    const [loading, setLoading] = useState(true);

    // Filter Repayments for Selected Loan
    const selectedLoanRepayments = selectedLoan 
        ? allRepayments.filter(r => r.loanId === selectedLoan.id).reverse() 
        : [];

    // Modal States
    const [isPersonSearchOpen, setIsPersonSearchOpen] = useState(false);
    const [isNewPersonModalOpen, setIsNewPersonModalOpen] = useState(false);
    
    // Form States
    const [newLoan, setNewLoan] = useState({
        date: new Date().toISOString().split('T')[0],
        personId: '',
        personName: '',
        loanAmount: '',
        installmentAmount: '',
        duration: '',
        narration: '',
        paymentMode: 'cash',
        resolutionNo: '',
        resolutionDate: new Date().toISOString().split('T')[0],
        voucherNo: ''
    });

    const [newPerson, setNewPerson] = useState({
        firstName: '',
        middleName: '',
        lastName: '',
        aadharNo: '',
        mobileNo: '',
        villageName: 'इनाम धामणी',
        title: 'Mr.'
    });

    // Repayment Form State
    const [repaymentForm, setRepaymentForm] = useState({
        repaymentAmount: '',
        date: new Date().toISOString().split('T')[0],
        rebateAmount: '0',
        narration: '',
        paymentMode: 'cash', // 'cash' or 'transfer'
        receiptNo: '',
        bankLedgerId: '',
        bankLedgerName: '',
        transactionNarration: ''
    });

    // Fetch Data on Mount
    useEffect(() => {
        const loansRef = ref(database, 'Grampanchayat/Panipuravtha/Transactions/Loans');
        const personsRef = ref(database, 'Grampanchayat/Gramniddhi/Certificates and licenses/PersonList');
        const repaymentsRef = ref(database, 'Grampanchayat/Panipuravtha/Transactions/LoanRepayments');

        const unsubscribeLoans = onValue(loansRef, (snapshot) => {
            const data = [];
            snapshot.forEach((child) => {
                data.push({ id: child.key, ...child.val() });
            });
            setLoans(data.reverse()); // Show newest first
            setLoading(false);
        });

        const unsubscribePersons = onValue(personsRef, (snapshot) => {
            const data = [];
            snapshot.forEach((child) => {
                data.push({ id: child.key, ...child.val() });
            });
            setPersons(data);
        });

        const unsubscribeRepayments = onValue(repaymentsRef, (snapshot) => {
            const data = [];
            snapshot.forEach((child) => {
                data.push({ id: child.key, ...child.val() }); // Include firebase key as id
            });
            setAllRepayments(data);
        });

        return () => {
            unsubscribeLoans();
            unsubscribePersons();
            unsubscribeRepayments();
        };
    }, []);

    const handleSaveNewPerson = () => {
        if (!newPerson.firstName || !newPerson.lastName) {
            alert('कृपया नाव आणि आडनाव भरा');
            return;
        }

        const personRef = ref(database, 'Grampanchayat/Gramniddhi/Certificates and licenses/PersonList');
        const fullName = `${newPerson.firstName} ${newPerson.middleName} ${newPerson.lastName}`.trim();
        
        const personData = {
            ...newPerson,
            fullName,
            createdAt: new Date().toISOString()
        };

        push(personRef, personData)
            .then((ref) => {
                alert('नवीन व्यक्ती यशस्वीरित्या जोडली!');
                setIsNewPersonModalOpen(false);
                setIsPersonSearchOpen(true);
                // Reset form
                setNewPerson({
                    firstName: '',
                    middleName: '',
                    lastName: '',
                    aadharNo: '',
                    mobileNo: '',
                    villageName: 'इनाम धामणी',
                    title: 'Mr.'
                });
            })
            .catch((err) => alert('Error: ' + err.message));
    };

    const handleSelectPerson = (person) => {
        setNewLoan(prev => ({
            ...prev,
            personId: person.id,
            personName: person.fullName
        }));
        setIsPersonSearchOpen(false);
    };

    const handleSaveNewLoan = () => {
        if (!newLoan.personName || !newLoan.loanAmount) {
            alert('कृपया व्यक्तीचे नाव आणि रक्कम भरा');
            return;
        }

        const loansRef = ref(database, 'Grampanchayat/Panipuravtha/Transactions/Loans');
        const loanData = {
            ...newLoan,
            balanceAmount: parseFloat(newLoan.loanAmount), // Initial balance = loan amount
            status: 'Active',
            createdAt: new Date().toISOString(),
            financialYear: arthikVarshLabel || '2024-2025' // Add financial year context
        };

        push(loansRef, loanData)
            .then(() => {
                alert('नवीन कर्ज यशस्वीरित्या नोंदवले!');
                setView('list');
                setNewLoan({ // Reset
                     date: new Date().toISOString().split('T')[0],
                    personId: '',
                    personName: '',
                    loanAmount: '',
                    installmentAmount: '',
                    duration: '',
                    narration: '',
                    paymentMode: 'cash',
                    resolutionNo: '',
                    resolutionDate: new Date().toISOString().split('T')[0],
                    voucherNo: ''
                });
            })
            .catch((err) => alert('Error: ' + err.message));
    };

    const handleRepaymentClick = (loan) => {
        setSelectedLoan(loan);
        setView('repayment_history'); // Go to History List first
    };

    const handleNewRepaymentClick = () => {
        // Prepare form for new entry
        setRepaymentForm({
            repaymentAmount: '',
            date: new Date().toISOString().split('T')[0],
            rebateAmount: '0',
            narration: '',
            paymentMode: 'cash',
            receiptNo: '', 
            transactionNarration: `Repayment for Loan: ${selectedLoan.personName}`,
            // Pre-fill bank/account info if needed
            bankLedgerId: '',
            bankLedgerName: ''
        });
        setView('repayment_form');
    };

    const handleSaveRepayment = () => {
        if (!repaymentForm.repaymentAmount || !selectedLoan) {
             alert('कृपया परतफेड रक्कम भरा');
             return;
        }

        const amount = parseFloat(repaymentForm.repaymentAmount);
        const newBalance = parseFloat(selectedLoan.balanceAmount) - amount;

        if (newBalance < 0) {
            alert('परतफेड रक्कम ही शिल्लक रक्कमेपेक्षा जास्त असू शकत नाही.');
            return;
        }

        // 1. Save Repayment Transaction
        const repaymentRef = ref(database, 'Grampanchayat/Panipuravtha/Transactions/LoanRepayments');
        const repaymentData = {
            loanId: selectedLoan.id,
            personName: selectedLoan.personName,
            ...repaymentForm,
            amount: amount,
            previousBalance: selectedLoan.balanceAmount,
            newBalance: newBalance,
            createdAt: new Date().toISOString()
        };

        push(repaymentRef, repaymentData);

        // 2. Update Loan Balance
        const loanRef = ref(database, `Grampanchayat/Panipuravtha/Transactions/Loans/${selectedLoan.id}`);
        update(loanRef, {
            balanceAmount: newBalance,
            lastRepaymentDate: repaymentForm.date,
            status: newBalance <= 0 ? 'Closed' : 'Active'
        })
        .then(() => {
            alert('परतफेड यशस्वीरित्या नोंदवली!');
            setView('repayment_history'); // Return to history list
        })
        .catch((err) => alert('Error: ' + err.message));
    };

    // Filter Logic for Person Search
    const [personFilter, setPersonFilter] = useState({ name: '', aadhar: '' });
    const filteredPersons = persons.filter(p => {
        const matchName = !personFilter.name || (p.fullName && p.fullName.toLowerCase().includes(personFilter.name.toLowerCase()));
        const matchAadhar = !personFilter.aadhar || (p.aadharNo && p.aadharNo.includes(personFilter.aadhar));
        return matchName && matchAadhar;
    });

    return (
        <div className="min-h-screen bg-gray-50 font-poppins text-sm">
            {/* Header */}
            <div className="bg-blue-800 text-white p-4 flex justify-between items-center shadow-md">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => {
                            if (view === 'list') navigate(-1);
                            else if (view === 'repayment_form') setView('repayment_history');
                            else if (view === 'repayment_history') setView('list');
                            else setView('list');
                        }}
                        className="hover:bg-blue-700 p-2 rounded-full transition-colors"
                    >
                        <FiArrowLeft className="text-xl" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold">
                            {view === 'repayment_history' || view === 'repayment_form' ? 'आगाऊ रक्कम परतफेड माहिती' : 'आगाऊ रक्कम माहिती'}
                        </h1>
                        <p className="text-xs text-blue-200">
                             {grampanchayatName ? `${grampanchayatName} | ` : ''}आर्थिक वर्ष: {arthikVarshLabel || '२०२४-२५'}
                        </p>
                    </div>
                </div>
                 <div className="flex gap-2">
                    {view === 'list' && (
                        <button 
                            onClick={() => setView('new')} 
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold shadow"
                        >
                            नवीन
                        </button>
                    )}
                    {view === 'repayment_history' && (
                         <button 
                            onClick={handleNewRepaymentClick}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-bold shadow"
                        >
                            नवीन
                        </button>
                    )}
                 </div>
            </div>

            <div className="p-4 max-w-7xl mx-auto">
                {view === 'list' && (
                    <div className="bg-white rounded-lg shadow border border-blue-200 overflow-hidden">
                        {/* Search Bar */}
                        <div className="flex p-4 gap-2 border-b border-gray-100 bg-blue-50">
                            <input 
                                type="text" 
                                placeholder="शोधणे" 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border border-blue-300 p-2 rounded w-full md:w-1/2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button className="bg-green-600 text-white px-6 py-2 rounded font-medium hover:bg-green-700">
                                शोधणे
                            </button>
                        </div>

                        {/* Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-blue-800 text-white">
                                    <tr>
                                        <th className="p-3 border border-blue-700 text-center">कर्ज नं.</th>
                                        <th className="p-3 border border-blue-700">दिनांक</th>
                                        <th className="p-3 border border-blue-700">व्यक्तीचे नाव</th>
                                        <th className="p-3 border border-blue-700 text-right">आगाऊ रक्कम</th>
                                        <th className="p-3 border border-blue-700">शेरा</th>
                                        <th className="p-3 border border-blue-700 text-center">पर्याय</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loans.map((loan, index) => (
                                        <tr key={loan.id} className="hover:bg-blue-50 border-b border-blue-100">
                                            <td className="p-3 border border-blue-100 text-center">{index + 1}</td>
                                            <td className="p-3 border border-blue-100">{new Date(loan.date).toLocaleDateString('en-GB')}</td>
                                            <td className="p-3 border border-blue-100 font-medium">{loan.personName}</td>
                                            <td className="p-3 border border-blue-100 text-right">{loan.loanAmount}</td>
                                            <td className="p-3 border border-blue-100">{loan.narration}</td>
                                            <td className="p-3 border border-blue-100">
                                                <div className="flex justify-center gap-2">
                                                    <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs">
                                                        खोडणे
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            setSelectedLoan(loan);
                                                            setPrintMode('namuna12');
                                                            setTimeout(() => window.print(), 100);
                                                        }}
                                                        className="bg-blue-700 hover:bg-blue-800 text-white px-3 py-1 rounded text-xs"
                                                    >
                                                        नमुना नं. 12
                                                    </button>
                                                    <button 
                                                        onClick={() => {
                                                            setPrintMode('namuna17');
                                                        }}
                                                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded text-xs"
                                                    >
                                                        नमुना १७
                                                    </button>
                                                    <button 
                                                        onClick={() => handleRepaymentClick(loan)}
                                                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                                                    >
                                                        परतफेड
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination (Placeholder) */}
                         <div className="bg-blue-800 p-2 flex justify-center gap-1">
                            <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">|&lt;</button>
                            <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">&lt;</button>
                            <button className="px-3 py-1 bg-white text-blue-900 rounded font-bold text-xs">1</button>
                            <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">&gt;</button>
                            <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">&gt;|</button>
                        </div>
                    </div>
                )}

                {/* Print Layout for Namuna 12 */}
                {printMode === 'namuna12' && (
                    <div className="hidden print:block fixed inset-0 bg-white z-[100] p-8">
                        {selectedLoan && (
                            <div className="flex flex-col gap-8 h-full">
                                {/* Copy 1 */}
                                <div className="border-2 border-black rounded-xl p-6 relative">
                                    <div className="text-center font-bold text-lg mb-2">ग्रामपंचायत {grampanchayatName || 'इनाम धामणी'}</div>
                                    <div className="text-center font-bold text-xl mb-6">नमुना १२</div>
                                    
                                    <div className="flex justify-between mb-4">
                                        <span>तारीख : {new Date().toLocaleDateString('en-GB')}</span>
                                    </div>

                                    <div className="border-t border-b border-black py-2 mb-4">
                                        <span>प्रमाणक (व्हार्ऊचर) क्रमांक :- {selectedLoan.voucherNo || '-'}</span>
                                    </div>

                                    <div className="flex justify-between mb-4">
                                        <span>ठराव क्रमांक : {selectedLoan.resolutionNo || '-'}</span>
                                        <span>तारीख : {selectedLoan.resolutionDate ? new Date(selectedLoan.resolutionDate).toLocaleDateString('en-GB') : '-'}</span>
                                    </div>

                                    <div className="mb-4">
                                        <span>व्यक्तीचे नाव : </span>
                                        <span className="font-medium ml-2">{selectedLoan.personName}</span>
                                    </div>

                                    <div className="mb-8 leading-loose">
                                        बिलात दर्शविलेली रुपये <span className="font-bold border-b border-black px-2">{parseFloat(selectedLoan.loanAmount).toFixed(2)}</span> ची रक्कम मंजूर करण्यात येत आहे.<br/>
                                        दाव्याच्या पूर्ण फेडीबद्दल रू. <span className="font-bold border-b border-black px-2">{parseFloat(selectedLoan.loanAmount).toFixed(2)}</span> (अक्षरी) ____________________<br/> ____________________ फक्त ची रक्कम मिळाली.
                                    </div>

                                    <div className="mb-12">
                                        माझ्यासमोर रक्कम दिली <span className="font-bold">{parseFloat(selectedLoan.loanAmount).toFixed(2)}</span>
                                    </div>

                                    <div className="flex justify-between items-end mt-16">
                                        <div className="text-center">
                                            <div className="font-bold mb-8">सरपंच : </div>
                                        </div>
                                        
                                         {/* Stamp Box */}
                                        <div className="border border-black w-24 h-24 flex items-center justify-center">
                                            मुद्रांक
                                        </div>

                                        <div className="text-center">
                                             <div className="border-t border-black w-40 mt-8 pt-2">पैसे घेणाराची सही</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Separator / Cut Line */}
                                <div className="border-b-2 border-dashed border-gray-400 my-4 flex justify-center sticky">
                                    <span className="bg-white px-2 text-gray-500 text-xs -mt-2">Cut Here</span>
                                </div>

                                 {/* Copy 2 (Duplicate) */}
                                <div className="border-2 border-black rounded-xl p-6 relative">
                                    <div className="text-center font-bold text-lg mb-2">ग्रामपंचायत {grampanchayatName || 'इनाम धामणी'}</div>
                                    <div className="text-center font-bold text-xl mb-6">नमुना १२</div>
                                    
                                    <div className="flex justify-between mb-4">
                                        <span>तारीख : {new Date().toLocaleDateString('en-GB')}</span>
                                    </div>

                                    <div className="border-t border-b border-black py-2 mb-4">
                                        <span>प्र प्रमाणक (व्हार्ऊचर) क्रमांक :- {selectedLoan.voucherNo || '-'}</span>
                                    </div>

                                    <div className="flex justify-between mb-4">
                                        <span>ठराव क्रमांक : {selectedLoan.resolutionNo || '-'}</span>
                                        <span>तारीख : {selectedLoan.resolutionDate ? new Date(selectedLoan.resolutionDate).toLocaleDateString('en-GB') : '-'}</span>
                                    </div>

                                    <div className="mb-4">
                                        <span>व्यक्तीचे नाव : </span>
                                        <span className="font-medium ml-2">{selectedLoan.personName}</span>
                                    </div>

                                    <div className="mb-8 leading-loose">
                                        बिलात दर्शविलेली रुपये <span className="font-bold border-b border-black px-2">{parseFloat(selectedLoan.loanAmount).toFixed(2)}</span> ची रक्कम मंजूर करण्यात येत आहे.<br/>
                                        दाव्याच्या पूर्ण फेडीबद्दल रू. <span className="font-bold border-b border-black px-2">{parseFloat(selectedLoan.loanAmount).toFixed(2)}</span> (अक्षरी) ____________________<br/> ____________________ फक्त ची रक्कम मिळाली.
                                    </div>

                                    <div className="mb-12">
                                        माझ्यासमोर रक्कम दिली <span className="font-bold">{parseFloat(selectedLoan.loanAmount).toFixed(2)}</span>
                                    </div>

                                    <div className="flex justify-between items-end mt-16">
                                        <div className="text-center">
                                            <div className="font-bold mb-8">सरपंच : </div>
                                        </div>
                                        
                                         {/* Stamp Box */}
                                        <div className="border border-black w-24 h-24 flex items-center justify-center">
                                            मुद्रांक
                                        </div>

                                        <div className="text-center">
                                             <div className="border-t border-black w-40 mt-8 pt-2">पैसे घेणाराची सही</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Print Layout for Namuna 17 (Loan Register) moved to Namuna17Report.js */}

                {/* Repayment History View (Image 1) */}
                {view === 'repayment_history' && selectedLoan && (
                    <div className="space-y-6">
                        {/* Loan Info Header */}
                        <div className="bg-white rounded border border-blue-200 overflow-hidden">
                            <div className="bg-blue-800 text-white px-4 py-2 font-semibold">
                                कर्ज माहिती
                            </div>
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">कर्ज नं.</span>
                                    <span className="font-bold">1</span> {/* Just index/id placeholder */}
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">कर्ज रक्कम</span>
                                    <span className="font-bold">{selectedLoan.loanAmount}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">व्यक्तीचे नाव</span>
                                    <span className="font-bold">{selectedLoan.personName}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">शिल्लक रक्कम</span>
                                    <span className="font-bold">{selectedLoan.balanceAmount}</span>
                                </div>
                            </div>
                        </div>

                         {/* Repayment List Table */}
                         <div className="bg-white rounded border border-blue-200 overflow-hidden shadow-lg">
                            <div className="bg-blue-800 text-white px-4 py-2 font-semibold flex justify-between items-center">
                                <span>आगाऊ रक्कम परतफेड माहिती</span>
                                {/* 'New' button is in header in image 1, but user said 'when click on new then again open...' */}
                            </div>
                            
                            {/* Search inside Repayment List */}
                            <div className="flex p-4 gap-2 bg-blue-50 border-b border-gray-100">
                                <input 
                                    type="text" 
                                    placeholder="शोधणे" 
                                    className="border border-blue-300 p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button className="bg-green-600 text-white px-6 py-2 rounded font-medium hover:bg-green-700">
                                    शोधणे
                                </button>
                            </div>

                             <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="bg-blue-800 text-white text-sm">
                                        <tr>
                                            <th className="p-3 border border-blue-700 text-center">कर्ज क्र.</th>
                                            <th className="p-3 border border-blue-700 text-center">दिनांक</th>
                                            <th className="p-3 border border-blue-700 text-center">परतफेड रक्कम</th>
                                            <th className="p-3 border border-blue-700 text-center">शेरा</th>
                                            <th className="p-3 border border-blue-700 text-center">पर्याय</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm">
                                        {selectedLoanRepayments.length > 0 ? (
                                            selectedLoanRepayments.map((rep, idx) => (
                                                <tr key={rep.id || idx} className="hover:bg-blue-50 border-b border-blue-100">
                                                    <td className="p-3 border border-blue-100 text-center">{idx + 1}</td>
                                                    <td className="p-3 border border-blue-100 text-center">{new Date(rep.date).toLocaleDateString('en-GB')}</td>
                                                    <td className="p-3 border border-blue-100 text-center">{rep.amount}</td>
                                                    <td className="p-3 border border-blue-100 text-center">{rep.narration || '-'}</td>
                                                    <td className="p-3 border border-blue-100 text-center">
                                                        <div className="flex justify-center gap-2">
                                                            <button className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs font-bold">
                                                                खोडणे
                                                            </button>
                                                            <button 
                                                                onClick={() => {
                                                                    const receiptData = {
                                                                        ...rep,
                                                                        challanNo: rep.receiptNo || '-',
                                                                        personName: selectedLoan.personName,
                                                                        customContent: 'कडून आगाऊ दिलेल्या रक्कमा च्या खाती रक्कम रुपये',
                                                                        voucherType: 'कर्ज परतफेड',
                                                                        totalAmount: parseFloat(rep.amount)
                                                                    };
                                                                    setSelectedRepayment(receiptData);
                                                                    setPrintMode('namuna7');
                                                                }}
                                                                className="bg-blue-800 hover:bg-blue-900 text-white px-3 py-1 rounded text-xs font-bold"
                                                            >
                                                                नमुना नं. 7
                                                            </button>
                                                            <button 
                                                                onClick={() => setPrintMode('namuna17')}
                                                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-bold"
                                                            >
                                                                रिपोर्ट
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="p-4 text-center text-gray-500">No repayments found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination (Placeholder) */}
                             <div className="bg-blue-800 p-2 flex justify-center gap-1">
                                <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">|&lt;</button>
                                <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">&lt;</button>
                                <button className="px-3 py-1 bg-white text-blue-900 rounded font-bold text-xs">1</button>
                                <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">&gt;</button>
                                <button className="px-3 py-1 bg-white/20 text-white rounded hover:bg-white/30 text-xs">&gt;|</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Repayment Form View (Image 2) */}
                {view === 'repayment_form' && selectedLoan && (
                    <div className="space-y-6">
                        {/* Info Section (Same as History View) */}
                         <div className="bg-white rounded border border-blue-200 overflow-hidden">
                            <div className="bg-blue-800 text-white px-4 py-2 font-semibold">
                                आगाऊ रक्कम माहिती
                            </div>
                            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">कर्ज नं.</span>
                                    <span className="font-bold">1</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">कर्ज रक्कम</span>
                                    <span className="font-bold">{selectedLoan.loanAmount}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">व्यक्तीचे नाव</span>
                                    <span className="font-bold">{selectedLoan.personName}</span>
                                </div>
                                <div className="flex justify-between border-b border-gray-100 pb-1">
                                    <span className="text-gray-600">शिल्लक रक्कम</span>
                                    <span className="font-bold">{selectedLoan.balanceAmount}</span>
                                </div>
                            </div>
                        </div>

                        {/* Repayment Details Form */}
                        <div className="bg-white rounded border border-blue-200 overflow-hidden shadow-lg">
                            <div className="bg-blue-800 text-white px-4 py-2 font-semibold">
                                आगाऊ रक्कम परत माहिती
                            </div>
                            <div className="p-6 space-y-6">
                                {/* Row 1 */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">परतफेड रक्कम:</label>
                                        <input 
                                            type="number"
                                            value={repaymentForm.repaymentAmount}
                                            onChange={(e) => setRepaymentForm({...repaymentForm, repaymentAmount: e.target.value})}
                                            placeholder="परतफेड रक्कम भरा."
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                          <label className="w-32 text-gray-700 font-medium">सुट रक्कम:</label>
                                          <input 
                                            type="number"
                                            value={repaymentForm.rebateAmount}
                                            onChange={(e) => setRepaymentForm({...repaymentForm, rebateAmount: e.target.value})}
                                            placeholder="Please Enter Rebate Amount"
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                          />
                                    </div>
                                </div>

                                {/* Row 2 */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">दिनांक:</label>
                                        <input 
                                            type="date"
                                            value={repaymentForm.date}
                                            onChange={(e) => setRepaymentForm({...repaymentForm, date: e.target.value})}
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                    <div></div>
                                </div>

                                {/* Row 3: Narration */}
                                <div className="flex">
                                    <label className="w-32 text-gray-700 font-medium mt-2">शेरा:</label>
                                    <textarea 
                                        value={repaymentForm.narration}
                                        onChange={(e) => setRepaymentForm({...repaymentForm, narration: e.target.value})}
                                        placeholder="शेरा भरा."
                                        rows="2"
                                        className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                    ></textarea>
                                </div>

                                {/* Row 4: Mode */}
                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">व्यवहार प्रकार</label>
                                    <div className="flex items-center gap-6">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="paymentMode" 
                                                checked={repaymentForm.paymentMode === 'cash'}
                                                onChange={() => setRepaymentForm({...repaymentForm, paymentMode: 'cash'})}
                                                className="w-5 h-5 text-blue-800"
                                            />
                                            <span>रोख</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="paymentMode" 
                                                checked={repaymentForm.paymentMode === 'transfer'}
                                                onChange={() => setRepaymentForm({...repaymentForm, paymentMode: 'transfer'})}
                                                className="w-5 h-5 text-blue-800"
                                            />
                                            <span>वर्ग / ट्रान्सफर</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Transaction Details Section */}
                                <div className="bg-blue-50 p-4 rounded border border-blue-200">
                                    <h3 className="bg-blue-700 text-white px-3 py-1 inline-block rounded-t text-sm font-bold mb-4">व्यवहार तपशील</h3>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 gap-x-12">
                                        {/* Left Column */}
                                        <div className="space-y-4">
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">पावती नं. :</label>
                                                <input 
                                                    value={repaymentForm.receiptNo}
                                                    onChange={(e) => setRepaymentForm({...repaymentForm, receiptNo: e.target.value})}
                                                    placeholder="भरा."
                                                    className="border p-2 rounded w-full bg-white"
                                                />
                                            </div>
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">व्यक्तीचे नाव :</label>
                                                <input 
                                                    value={selectedLoan.personName}
                                                    readOnly
                                                    className="border p-2 rounded w-full bg-gray-200 text-gray-600"
                                                />
                                            </div>
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">खातावणी :</label>
                                                <div className="flex gap-1 flex-1">
                                                    <input value="541" readOnly className="border p-2 w-16 bg-gray-200 text-center" />
                                                    <button className="bg-blue-700 text-white px-3 rounded">...</button>
                                                    <input value="आगाऊ दिलेल्या रक्कम" readOnly className="border p-2 flex-1 bg-gray-200" />
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">खाते :</label>
                                                <div className="flex gap-1 flex-1">
                                                    <input value="1001" readOnly className="border p-2 w-16 bg-gray-200 text-center" />
                                                    <button className="bg-blue-700 text-white px-3 rounded">...</button>
                                                    <input value={`${selectedLoan.personName} Shinde`} readOnly className="border p-2 flex-1 bg-gray-200" />
                                                </div>
                                            </div>
                                             <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">व्यवहार रक्कम :</label>
                                                <input 
                                                    value={repaymentForm.repaymentAmount}
                                                    readOnly
                                                    placeholder="रक्कम भरा."
                                                    className="border p-2 rounded w-full bg-gray-200"
                                                />
                                            </div>
                                        </div>

                                        {/* Right Column */}
                                        <div className="space-y-4">
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">दिनांक :</label>
                                                <input 
                                                    type="date"
                                                    value={repaymentForm.date}
                                                    readOnly
                                                    className="border p-2 rounded w-full bg-gray-200"
                                                />
                                            </div>
                                            {/* Spacer for alignment */}
                                            <div className="h-10 hidden md:block"></div> 
                                            
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">वाटणीची रक्कम :</label>
                                                <input 
                                                    value="0"
                                                    readOnly
                                                    className="border p-2 rounded w-full bg-gray-200"
                                                />
                                            </div>
                                            <div className="flex items-center">
                                                <label className="w-32 text-gray-700 text-sm">खाते शिल्लक :</label>
                                                <input 
                                                    value={selectedLoan.balanceAmount}
                                                    readOnly
                                                    className="border p-2 rounded w-full bg-gray-200"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Footer Buttons */}
                            <div className="bg-blue-800 p-4 flex justify-center gap-4">
                                <button 
                                    onClick={handleSaveRepayment}
                                    className="bg-green-600 hover:bg-green-700 text-white px-8 py-2 rounded font-bold transition-colors"
                                >
                                    साठविणे
                                </button>
                                <button 
                                    onClick={() => setView('repayment_history')} // Cancel goes back to history
                                    className="bg-red-500 hover:bg-red-600 text-white px-8 py-2 rounded font-bold transition-colors"
                                >
                                    रद्द करणे
                                </button>
                            </div>
                        </div>
                    </div>
                )}


                {view === 'new' && (
                    <div className="space-y-6">
                        {/* Section 1: Loan Information */}
                        <div className="bg-white rounded border border-blue-200 overflow-hidden shadow-lg">
                            <div className="bg-blue-800 text-white px-4 py-2 font-semibold">
                                आगाऊ रक्कम माहिती
                            </div>
                            <div className="p-6 space-y-6">
                                {/* Row 1: Loan No & Date */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">कर्ज नं.:</label>
                                        <input 
                                            type="text"
                                            value="Auto Generated"
                                            readOnly
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 bg-gray-200 text-gray-600"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium text-right pr-4">दिनांक:</label>
                                        <input 
                                            type="date"
                                            value={newLoan.date}
                                            onChange={(e) => setNewLoan({...newLoan, date: e.target.value})}
                                            className="border border-blue-300 rounded px-3 py-2 w-48 focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                </div>

                                {/* Row 2: Person Name */}
                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">व्यक्तीचे नाव :</label>
                                    <div className="flex-1 flex gap-0">
                                        <input 
                                            onClick={() => setIsPersonSearchOpen(true)}
                                            type="text"
                                            value={newLoan.personName}
                                            readOnly
                                            placeholder="व्यक्तीचे नाव निवडा"
                                            className="border border-blue-300 rounded-l px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-gray-100 cursor-pointer"
                                        />
                                        <button 
                                            onClick={() => setIsPersonSearchOpen(true)}
                                            className="bg-blue-800 text-white px-4 rounded-r hover:bg-blue-700"
                                        >
                                            ...
                                        </button>
                                    </div>
                                </div>

                                {/* Row 3: Loan Amount & Opening Balance */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">कर्ज रक्कम:</label>
                                        <input 
                                            type="number"
                                            value={newLoan.loanAmount}
                                            onChange={(e) => setNewLoan({...newLoan, loanAmount: e.target.value})}
                                            placeholder="कर्ज रक्कम भरा."
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium text-right pr-4">आरंभीची शिल्लक:</label>
                                        <input 
                                            type="number"
                                            value={newLoan.loanAmount || 0}
                                            readOnly
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 bg-gray-200 text-gray-600"
                                        />
                                    </div>
                                </div>

                                {/* Row 4: Installment & Duration */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium">हफ्ता रक्कम:</label>
                                        <input 
                                            type="number"
                                            value={newLoan.installmentAmount}
                                            onChange={(e) => setNewLoan({...newLoan, installmentAmount: e.target.value})}
                                            placeholder="हफ्ता रक्कम भरा."
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                    <div className="flex items-center">
                                        <label className="w-32 text-gray-700 font-medium text-right pr-4">कालावधी:</label>
                                        <input 
                                            type="text"
                                            value={newLoan.duration}
                                            onChange={(e) => setNewLoan({...newLoan, duration: e.target.value})}
                                            placeholder="कालावधी महिने भरा."
                                            className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                        />
                                    </div>
                                </div>

                                {/* Row 5: Account No */}
                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">खाते क्र.:</label>
                                    <input 
                                        type="text"
                                        value="Auto generated"
                                        readOnly
                                        className="border border-blue-300 rounded px-3 py-2 w-1/3 bg-gray-200 text-gray-600"
                                    />
                                </div>

                                {/* Row 6: Narration */}
                                <div className="flex">
                                    <label className="w-32 text-gray-700 font-medium mt-2">शेरा:</label>
                                    <textarea 
                                        value={newLoan.narration}
                                        onChange={(e) => setNewLoan({...newLoan, narration: e.target.value})}
                                        placeholder="शेरा माहिती भरा."
                                        rows="2"
                                        className="border border-blue-300 rounded px-3 py-2 flex-1 focus:ring-2 focus:ring-blue-500 bg-white"
                                    ></textarea>
                                </div>

                                {/* Row 7: Payment Mode */}
                                <div className="flex items-center">
                                    <label className="w-32 text-gray-700 font-medium">व्यवहार प्रकार</label>
                                    <div className="flex items-center gap-6">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="newLoanPaymentMode" 
                                                checked={newLoan.paymentMode === 'cash'}
                                                onChange={() => setNewLoan({...newLoan, paymentMode: 'cash'})}
                                                className="w-5 h-5 text-blue-800"
                                            />
                                            <span>रोख</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input 
                                                type="radio" 
                                                name="newLoanPaymentMode" 
                                                checked={newLoan.paymentMode === 'transfer'}
                                                onChange={() => setNewLoan({...newLoan, paymentMode: 'transfer'})}
                                                className="w-5 h-5 text-blue-800"
                                            />
                                            <span>वर्ग / ट्रान्सफर</span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Section 2: Transaction Details */}
                        <div className="bg-white rounded border border-blue-200 overflow-hidden shadow-lg">
                            <div className="bg-blue-800 text-white px-4 py-2 font-semibold">
                                व्यवहार तपशील
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 gap-x-12">
                                    {/* Left Column */}
                                    <div className="space-y-4">
                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm">व्हॉचर नं. :</label>
                                            <input 
                                                value={newLoan.voucherNo}
                                                onChange={(e) => setNewLoan({...newLoan, voucherNo: e.target.value})}
                                                placeholder="व्हॉचर नं."
                                                className="border p-2 rounded w-full bg-white border-blue-300"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm">व्यक्तीचे नाव :</label>
                                            <input 
                                                value={newLoan.personName}
                                                readOnly
                                                placeholder="व्यक्तीचे नाव भरा."
                                                className="border p-2 rounded w-full bg-gray-200 text-gray-600 border-blue-300"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm">ठराव नं. :</label>
                                            <input 
                                                value={newLoan.resolutionNo}
                                                onChange={(e) => setNewLoan({...newLoan, resolutionNo: e.target.value})}
                                                placeholder="ठराव नं. भरा."
                                                className="border p-2 rounded w-full bg-white border-blue-300"
                                            />
                                        </div>
                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm">खातावणी :</label>
                                            <div className="flex gap-1 flex-1">
                                                <input value="541" readOnly className="border p-2 w-16 bg-gray-200 text-center border-blue-300" />
                                                <button className="bg-blue-700 text-white px-3 rounded">...</button>
                                                <input value="आगाऊ दिलेल्या रक्कम" readOnly className="border p-2 flex-1 bg-gray-200 border-blue-300" />
                                            </div>
                                        </div>
                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm">खाते :</label>
                                            <div className="flex gap-1 flex-1">
                                                <input value="0" readOnly className="border p-2 w-16 bg-gray-200 text-center border-blue-300" />
                                                <button className="bg-blue-700 text-white px-3 rounded">...</button>
                                                <input value="नविन खाते" readOnly className="border p-2 flex-1 bg-gray-200 border-blue-300" />
                                            </div>
                                        </div>
                                         <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm">व्यवहार रक्कम :</label>
                                            <input 
                                                value={newLoan.loanAmount}
                                                readOnly
                                                placeholder="रक्कम भरा."
                                                className="border p-2 rounded w-full bg-gray-200 border-blue-300"
                                            />
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="space-y-4">
                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm text-right pr-4">दिनांक :</label>
                                            <input 
                                                type="date"
                                                value={newLoan.date}
                                                readOnly
                                                className="border p-2 rounded w-48 bg-gray-100 border-blue-300"
                                            />
                                        </div>
                                        {/* Spacer to align with Resolution No */}
                                         <div className="h-10 md:block hidden"></div>

                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm text-right pr-4">ठराव दिनांक:</label>
                                            <input 
                                                type="date"
                                                value={newLoan.resolutionDate}
                                                onChange={(e) => setNewLoan({...newLoan, resolutionDate: e.target.value})}
                                                className="border p-2 rounded w-48 bg-white border-blue-300"
                                            />
                                        </div>

                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm text-right pr-4">वाटणीची रक्कम :</label>
                                            <input 
                                                value="0"
                                                readOnly
                                                className="border p-2 rounded w-48 bg-gray-200 border-blue-300"
                                            />
                                        </div>

                                        <div className="flex items-center">
                                            <label className="w-32 text-gray-700 text-sm text-right pr-4">खाते शिल्लक :</label>
                                            <input 
                                                value={newLoan.loanAmount || 0}
                                                readOnly
                                                className="border p-2 rounded w-48 bg-gray-200 border-blue-300"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Footer Buttons */}
                            <div className="bg-blue-800 p-4 flex justify-center gap-4">
                                <button 
                                    onClick={handleSaveNewLoan}
                                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded font-bold"
                                >
                                    साठविणे
                                </button>
                                <button 
                                    onClick={() => setView('list')}
                                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded font-bold"
                                >
                                    रद्द करणे
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Person Search Modal */}
            {isPersonSearchOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl overflow-hidden font-poppins">
                        <div className="bg-blue-800 text-white px-4 py-2 flex justify-between items-center">
                            <h3 className="font-semibold">व्यक्तीची माहिती</h3>
                            <button onClick={() => setIsPersonSearchOpen(false)} className="hover:bg-blue-700 p-1 rounded">
                                <FiX className="text-xl" />
                            </button>
                        </div>
                        <div className="p-4">
                            {/* Search Filters */}
                            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 items-end">
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">आधार क्र.</label>
                                    <input 
                                        value={personFilter.aadhar}
                                        onChange={(e) => setPersonFilter({...personFilter, aadhar: e.target.value})}
                                        placeholder="आधार नं." className="border p-2 rounded w-full border-blue-300" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">नाव</label>
                                    <input 
                                        value={personFilter.name}
                                        onChange={(e) => setPersonFilter({...personFilter, name: e.target.value})}
                                        placeholder="नाव शोधा" className="border p-2 rounded w-full border-blue-300" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">वडिलांचे/पतीचे नाव</label>
                                    <input placeholder="वडिलांचे/पतीचे नाव" className="border p-2 rounded w-full border-blue-300" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">आडनाव</label>
                                    <input placeholder="आडनाव" className="border p-2 rounded w-full border-blue-300" />
                                </div>
                                <div>
                                    <label className="block text-gray-700 text-sm mb-1">पर्याय</label>
                                    <button 
                                        onClick={() => {
                                            setIsPersonSearchOpen(false);
                                            setIsNewPersonModalOpen(true);
                                        }}
                                        className="bg-blue-800 text-white w-full py-2 rounded hover:bg-blue-700 font-medium"
                                    >
                                        नवीन
                                    </button>
                                </div>
                            </div>

                            {/* Results Table */}
                            <div className="bg-blue-800 text-white grid grid-cols-5 p-2 font-medium text-sm">
                                <div className="border-r border-blue-600 px-2">व्यक्ती नं.</div>
                                <div className="border-r border-blue-600 px-2">नाव</div>
                                <div className="border-r border-blue-600 px-2">वडिल/पतीचे नाव</div>
                                <div className="border-r border-blue-600 px-2">आडनाव</div>
                                <div className="px-2">आधार नं.</div>
                            </div>
                            <div className="border border-blue-200 max-h-64 overflow-y-auto">
                                {filteredPersons.length > 0 ? (
                                    filteredPersons.map((person, index) => (
                                        <div 
                                            key={person.id} 
                                            onClick={() => handleSelectPerson(person)}
                                            className="grid grid-cols-5 p-2 text-sm border-b hover:bg-blue-50 cursor-pointer"
                                        >
                                            <div className="px-2">{index + 1}</div>
                                            <div className="px-2">{person.firstName}</div>
                                            <div className="px-2">{person.middleName}</div>
                                            <div className="px-2">{person.lastName}</div>
                                            <div className="px-2">{person.aadharNo}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-4 text-center text-gray-500">
                                        No records found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* New Person Entry Modal */}
            {isNewPersonModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl overflow-hidden font-poppins">
                        <div className="bg-blue-800 text-white px-4 py-2 font-semibold">
                            व्यक्तीची माहिती
                        </div>
                        <div className="p-8 space-y-6">
                            
                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">शीर्षक</label>
                                <select 
                                    value={newPerson.title}
                                    onChange={(e) => setNewPerson({...newPerson, title: e.target.value})}
                                    className="border border-blue-300 p-2 rounded flex-1 bg-white"
                                >
                                    <option>-</option>
                                    <option>Mr.</option>
                                    <option>Mrs.</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">पहिले नाव</label>
                                <input 
                                    value={newPerson.firstName}
                                    onChange={(e) => setNewPerson({...newPerson, firstName: e.target.value})}
                                    placeholder="पहिले नाव" className="border border-blue-300 p-2 rounded flex-1" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">वडिलांचे/पतीचे नाव</label>
                                <input 
                                    value={newPerson.middleName}
                                    onChange={(e) => setNewPerson({...newPerson, middleName: e.target.value})}
                                    placeholder="वडिलांचे/पतीचे नाव" className="border border-blue-300 p-2 rounded flex-1" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">आडनाव</label>
                                <input 
                                    value={newPerson.lastName}
                                    onChange={(e) => setNewPerson({...newPerson, lastName: e.target.value})}
                                    placeholder="आडनाव" className="border border-blue-300 p-2 rounded flex-1" 
                                />
                            </div>

                             <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">सफिक्स</label>
                                <select className="border border-blue-300 p-2 rounded flex-1 bg-white">
                                    <option>-</option>
                                </select>
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">ओळख चिन्ह</label>
                                <input placeholder="ओळख चिन्ह" className="border border-blue-300 p-2 rounded flex-1" />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">भ्रमणध्वनी क्र</label>
                                <input 
                                    value={newPerson.mobileNo}
                                    onChange={(e) => setNewPerson({...newPerson, mobileNo: e.target.value})}
                                    placeholder="भ्रमणध्वनी क्र" className="border border-blue-300 p-2 rounded flex-1" 
                                />
                            </div>

                            <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">आधार क्रमांक</label>
                                <input 
                                    value={newPerson.aadharNo}
                                    onChange={(e) => setNewPerson({...newPerson, aadharNo: e.target.value})}
                                    placeholder="आधार क्रमांक" className="border border-blue-300 p-2 rounded flex-1" 
                                />
                            </div>

                             <div className="flex items-center gap-4">
                                <label className="w-32 text-gray-700 font-medium">गावाचे नाव</label>
                                <select 
                                    value={newPerson.villageName}
                                    onChange={(e) => setNewPerson({...newPerson, villageName: e.target.value})}
                                    className="border border-blue-300 p-2 rounded flex-1 bg-white"
                                >
                                    <option>इनाम धामणी</option>
                                </select>
                            </div>

                             <div className="flex justify-center gap-4 mt-8 bg-blue-800 p-3 -mx-8 -mb-8">
                                <button 
                                    onClick={handleSaveNewPerson}
                                    className="bg-green-600 text-white px-6 py-2 rounded font-bold hover:bg-green-700"
                                >
                                    साठविणे
                                </button>
                                <button 
                                    onClick={() => {
                                        setIsNewPersonModalOpen(false);
                                        setIsPersonSearchOpen(true);
                                    }}
                                    className="bg-red-500 text-white px-6 py-2 rounded font-bold hover:bg-red-600"
                                >
                                    रद्द करा
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Print Receipt (Namuna 7) */}
            {printMode === 'namuna7' && selectedRepayment && (
                <Namuna7Receipt 
                    transaction={selectedRepayment} 
                    onPrintComplete={() => setPrintMode(null)} 
                />
            )}
            {/* Print Report (Namuna 17) */}
            {printMode === 'namuna17' && (
                <Namuna17Report 
                    loans={view === 'repayment_history' && selectedLoan ? [selectedLoan] : loans}
                    onPrintComplete={() => setPrintMode(null)} 
                />
            )}
        </div>
    );
};


export default LoanManagement;
